/**
 * Resolves the public API base URL (includes `/api` path) for the current environment.
 *
 * When the app is opened via a LAN IP or tunnel (e.g. http://10.13.2.5:3330) but
 * `NEXT_PUBLIC_API_ENDPOINT` points at `http://localhost:8881/api`, browsers would
 * call localhost on the *client* machine. Replacing the hostname with the page's
 * host fixes that.
 *
 * - SSR / Node: returns `NEXT_PUBLIC_API_ENDPOINT` unchanged.
 * - Browser: rewrites localhost/127.0.0.1 in the configured URL to `window.location.hostname`
 *   when the page is not on loopback, or when `NEXT_PUBLIC_API_FOLLOW_PAGE_HOST=true`.
 */
export function getPublicApiBaseUrl(): string {
  const configured = process.env.NEXT_PUBLIC_API_ENDPOINT?.trim() || '';

  if (typeof window === 'undefined') {
    return configured;
  }

  return rewriteLocalhostServiceUrl(configured);
}

/**
 * Same hostname rewrite for chatbot base URL (`NEXT_PUBLIC_CHATBOT_URL`).
 */
export function getPublicChatbotUrl(): string {
  const configured = process.env.NEXT_PUBLIC_CHATBOT_URL?.trim() || '';

  if (typeof window === 'undefined') {
    return configured;
  }

  return rewriteLocalhostServiceUrl(configured);
}

function rewriteLocalhostServiceUrl(configured: string): string {
  if (!configured) {
    return configured;
  }

  const explicitFollow = process.env.NEXT_PUBLIC_API_FOLLOW_PAGE_HOST === 'true';
  const pageHost = window.location.hostname;
  const isLoopbackPage = pageHost === 'localhost' || pageHost === '127.0.0.1';

  let shouldRewrite = explicitFollow;
  if (!shouldRewrite && !isLoopbackPage) {
    if (configured.includes('://localhost') || configured.includes('://127.0.0.1')) {
      shouldRewrite = true;
    }
  }

  if (!shouldRewrite) {
    return configured;
  }

  try {
    const href = configured.startsWith('http') ? configured : `http://${configured}`;
    const u = new URL(href);
    const portSegment = u.port ? `:${u.port}` : '';
    const pathAndQuery = `${u.pathname}${u.search}${u.hash}`;
    return `${window.location.protocol}//${pageHost}${portSegment}${pathAndQuery}`;
  } catch {
    return configured;
  }
}
